import React, { useState, useEffect, useRef } from 'react';
import { messageAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Messages.css';

export default function Messages() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    messageAPI.getConversations().then(r => setConversations(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeConv) return;
    messageAPI.getConversation(activeConv.id).then(r => setMessages(r.data.messages));
  }, [activeConv]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async e => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConv) return;
    const other = activeConv.participants.find(p => p.userId !== user.id);
    if (!other) return;
    setSending(true);
    try {
      const { data } = await messageAPI.send({ receiverId: other.userId, content: newMsg });
      setMessages(m => [...m, data]);
      setNewMsg('');
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const getOtherUser = (conv) => conv.participants.find(p => p.userId !== user.id)?.user;

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;

  return (
    <div className="messages-page">
      {/* Conversation List */}
      <aside className="conversations-list">
        <div className="conv-header"><h2>Messages</h2></div>
        {conversations.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px 20px' }}>
            <div className="empty-state-icon">💬</div>
            <p>No conversations yet</p>
          </div>
        ) : (
          conversations.map(conv => {
            const other = getOtherUser(conv);
            const lastMsg = conv.messages?.[0];
            return (
              <button
                key={conv.id}
                className={`conv-item ${activeConv?.id === conv.id ? 'active' : ''}`}
                onClick={() => setActiveConv(conv)}
              >
                <div className="avatar">
                  {other?.avatar
                    ? <img src={`http://localhost:5000${other.avatar}`} alt={other.firstName} />
                    : `${other?.firstName?.[0] || '?'}${other?.lastName?.[0] || ''}`
                  }
                </div>
                <div className="conv-info">
                  <div className="conv-name">
                    {other?.businessName || `${other?.firstName} ${other?.lastName}`}
                    {conv.unreadCount > 0 && <span className="unread-badge">{conv.unreadCount}</span>}
                  </div>
                  <div className="conv-last">{lastMsg?.content?.substring(0, 40) || 'No messages yet'}</div>
                </div>
              </button>
            );
          })
        )}
      </aside>

      {/* Chat Window */}
      <main className="chat-window">
        {!activeConv ? (
          <div className="chat-empty">
            <div className="empty-state-icon">💬</div>
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the left to start messaging.</p>
          </div>
        ) : (
          <>
            <div className="chat-header">
              {(() => {
                const other = getOtherUser(activeConv);
                return (
                  <>
                    <div className="avatar">
                      {other?.avatar
                        ? <img src={`http://localhost:5000${other.avatar}`} alt={other.firstName} />
                        : `${other?.firstName?.[0] || '?'}${other?.lastName?.[0] || ''}`
                      }
                    </div>
                    <div>
                      <div className="font-semibold">{other?.businessName || `${other?.firstName} ${other?.lastName}`}</div>
                      <div className="text-light text-sm">{other?.email}</div>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="chat-messages">
              {messages.map(msg => (
                <div key={msg.id} className={`message ${msg.senderId === user.id ? 'mine' : 'theirs'}`}>
                  <div className="message-bubble">{msg.content}</div>
                  <div className="message-time">{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <form className="chat-input" onSubmit={handleSend}>
              <input
                type="text"
                className="form-input"
                placeholder="Type a message..."
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                disabled={sending}
              />
              <button type="submit" className="btn btn-primary" disabled={sending || !newMsg.trim()}>
                {sending ? '⏳' : 'Send'}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
